import {
  DEFAULT_FEATURE_FLAGS,
  FEATURE_FLAG_KEYS,
} from "../services/feature-flags";
import {
  createStorageEnvelope,
  validateStorageEnvelope,
  type StorageEnvelope,
} from "./storage-envelope";
import {
  isJsonObject,
  isRecord,
  validationFailure,
  type JsonObject,
  type ValidationResult,
} from "./validation";

interface SyntheticSettingsV0 {
  readonly schemaVersion: 0;
  readonly data: {
    readonly values: JsonObject;
  };
  readonly key: string;
  readonly savedAt: string;
  readonly type: "settings";
}

function migrateSyntheticSettingsV0(
  value: Record<string, unknown>,
  source: string,
): ValidationResult<StorageEnvelope<"settings">> {
  if (value.type !== "settings") {
    return validationFailure(
      source,
      "$.type",
      'synthetic v0 migration supports only "settings"',
    );
  }
  if (typeof value.key !== "string") {
    return validationFailure(source, "$.key", "expected a string");
  }
  if (typeof value.savedAt !== "string") {
    return validationFailure(source, "$.savedAt", "expected a string");
  }
  if (!isRecord(value.data) || !isJsonObject(value.data.values)) {
    return validationFailure(
      source,
      "$.data.values",
      "expected an object containing only finite JSON values",
    );
  }

  const predecessor = value as unknown as SyntheticSettingsV0;
  return {
    ok: true,
    value: createStorageEnvelope(
      "settings",
      predecessor.key,
      predecessor.savedAt,
      predecessor.data,
    ),
  };
}

/**
 * A feature-flags record saved by an earlier build cannot know about a flag
 * that did not exist yet, and strict validation would reject the whole record
 * for it — losing everything the trainee had set.
 *
 * An unknown flag has no stored value, so it takes its default, which is what
 * a fresh install of this build would have given it. This is a migration in
 * everything but the version number: the stored shape changed, and the change
 * is handled here and tested rather than discovered in the field.
 */
function fillFlagsAddedSinceTheRecordWasSaved(
  value: Record<string, unknown>,
): Record<string, unknown> {
  if (value.recordType !== "feature-flags") return value;
  const data = value.data;
  if (!isRecord(data) || !isRecord(data.values)) return value;

  const values: Record<string, unknown> = { ...data.values };
  let filled = false;
  for (const key of FEATURE_FLAG_KEYS) {
    if (typeof values[key] !== "boolean") {
      values[key] = DEFAULT_FEATURE_FLAGS[key];
      filled = true;
    }
  }
  return filled ? { ...value, data: { ...data, values } } : value;
}

export function migrateStorageEnvelope(
  value: unknown,
  source: string,
): ValidationResult<StorageEnvelope> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  if (!("schemaVersion" in value)) {
    return validationFailure(
      source,
      "$.schemaVersion",
      "unversioned application data is not accepted",
    );
  }
  if (value.schemaVersion === 0) {
    return migrateSyntheticSettingsV0(value, source);
  }
  if (value.schemaVersion === 1) {
    return validateStorageEnvelope(
      fillFlagsAddedSinceTheRecordWasSaved(value),
      source,
    );
  }
  return validationFailure(
    source,
    "$.schemaVersion",
    `no migration registered for schemaVersion ${String(value.schemaVersion)}`,
  );
}

export function parseAndMigrateStorageEnvelope(
  serialized: string,
  source: string,
): ValidationResult<StorageEnvelope> {
  let value: unknown;
  try {
    value = JSON.parse(serialized) as unknown;
  } catch {
    return validationFailure(source, "$", "invalid JSON");
  }
  return migrateStorageEnvelope(value, source);
}
